import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { HiArrowLeft } from 'react-icons/hi'

import {
  Divider,
  Flex,
  HStack,
  IconButton,
  Skeleton,
  Spacer,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'

import {
  Box as DarkBox,
  IconButton as DarkIconButton,
  DarkProvider,
} from '@hivewatch/dark'

import { DrawerBreadcrumb } from '@/components/ui'
import { StandardOperatingProcedureQuery } from '@/graphql/generated/operations'
import { formatDate } from '@/utils/formatDateTime'

interface SOPDrawerHeaderIProps {
  sop: StandardOperatingProcedureQuery['standardOperatingProcedure'] | undefined
  isLoading: boolean
  onClose: () => void
}

export const SOPDrawerHeader = ({
  sop,
  isLoading,
  onClose,
}: SOPDrawerHeaderIProps) => {
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const mode = useLightMode()

  const creatorFullName = sop?.creator
    ? `${sop?.creator?.firstName} ${sop?.creator?.lastName}`
    : ''

  if (isRebrand) {
    const prefix = mode === 'light' ? 'light.' : ''
    return (
      <DarkProvider>
        <DarkBox
          alignItems='center'
          display='flex'
          gap='12px'
          height='64px'
          pb='16px'
          pt='16px'
          px='24px'
        >
          <DarkIconButton
            _active={{ bg: 'transparent' }}
            aria-label='Back button'
            colorScheme={mode}
            dimension={36}
            hoverBgColor='transparent'
            icon={<HiArrowLeft size={18} />}
            onClick={onClose}
          />
          <DarkBox
            as='span'
            color={`${prefix}text.primary`}
            flex='1'
            fontFamily="'Soehne Kraftig', 'Söhne Kräftig', sans-serif"
            fontSize='20px'
            fontWeight='500'
            lineHeight='28px'
            minWidth={0}
            overflow='hidden'
            textOverflow='ellipsis'
            whiteSpace='nowrap'
          >
            {isLoading && !sop ? <Skeleton h='20px' w='120px' /> : sop?.name}
          </DarkBox>
          <DarkBox alignItems='center' display='flex' gap='16px'>
            <DarkInfoBox
              isLoading={isLoading}
              label='Creator'
              prefix={prefix}
              text={creatorFullName}
            />
            <DarkBox
              bg={`${prefix}border.subtle`}
              flexShrink={0}
              height='32px'
              width='1px'
            />
            <DarkInfoBox
              isLoading={isLoading}
              label='Date Created'
              prefix={prefix}
              text={formatDate(sop?.createdAt)}
            />
          </DarkBox>
        </DarkBox>
      </DarkProvider>
    )
  }

  return (
    <Flex
      alignItems='center'
      flexDirection='row'
      h='64px'
      pl='24px'
      pr='40px'
      w='full'
    >
      <HStack borderRight='1px solid #D5DCE4' height='80%' pr={3}>
        <IconButton
          aria-label='Back button'
          borderColor='transparent'
          borderRadius='md'
          h='30px'
          icon={<HiArrowLeft size={18} />}
          minWidth='30px'
          onClick={onClose}
          variant='ghost'
          w='30px'
        />
        <DrawerBreadcrumb
          hideDivider={true}
          isLoading={isLoading && !sop}
          label={sop?.name}
          maxWidth='175px'
        />
      </HStack>
      <Spacer />
      <HStack height='80%' spacing={4}>
        <InfoBox
          isLoading={isLoading}
          label='Creator'
          showTooltip={true}
          text={creatorFullName}
        />
        <Divider borderColor='#D7D4D4' orientation='vertical' w='1px' />
        <InfoBox
          isLoading={isLoading}
          label='Date Created'
          text={formatDate(sop?.createdAt)}
        />
      </HStack>
    </Flex>
  )
}

const DarkInfoBox = ({
  label,
  text,
  isLoading,
  prefix,
}: {
  label: string
  text: string
  isLoading: boolean
  prefix: string
}) => (
  <DarkBox display='flex' flexDirection='column' gap='2px'>
    {isLoading && !text ? (
      <Skeleton h='16px' w='80px' />
    ) : (
      <>
        <DarkBox
          as='span'
          color={`${prefix}text.tertiary`}
          fontFamily="'Soehne', sans-serif"
          fontSize='11px'
          fontWeight='400'
          lineHeight='14px'
        >
          {label}
        </DarkBox>
        <DarkBox
          as='span'
          color={`${prefix}text.primary`}
          fontFamily="'Soehne', sans-serif"
          fontSize='14px'
          fontWeight='500'
          lineHeight='18px'
          maxWidth='170px'
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
        >
          {text}
        </DarkBox>
      </>
    )}
  </DarkBox>
)

const InfoBox = ({
  label,
  text,
  isLoading,
  showTooltip = false,
}: {
  label: string
  text: string
  isLoading: boolean
  showTooltip?: boolean
}) => (
  <VStack
    align='start'
    justifyContent='center'
    letterSpacing='-0.37px'
    spacing={0}
  >
    {isLoading && !text ? (
      <Skeleton h='16px' w='80px' />
    ) : (
      <>
        <Text fontSize='11px' fontWeight='300'>
          {label}
        </Text>
        <Tooltip
          bgColor='#fff'
          boxShadow='md'
          color='#1a202c'
          display={showTooltip ? 'auto' : 'none'}
          fontSize='16px'
          fontWeight='semibold'
          label={text}
          letterSpacing='-0.65px'
          p='2'
          placement='bottom'
          rounded='5px'
        >
          <Text
            fontSize='14px'
            fontWeight='500'
            maxW='170px'
            minH='14px'
            noOfLines={1}
          >
            {text}
          </Text>
        </Tooltip>
      </>
    )}
  </VStack>
)
